import React, { useState, useCallback, useMemo } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { Stepper } from '@heartinternet/hi-ui';
import { Step1Fields } from '../HostingPackageTypeForm/Step1Fields';
import { Step2WebTools } from '../HostingPackageTypeForm/Step2WebTools';
import { Step3WebApps } from '../HostingPackageTypeForm/Step3WebApps';
import type { ResoldTypeProto, WebServerType } from '../HostingPackageTypeForm/types';
import type { HostingPackageTypeWizardPageProps } from './types';
import './HostingPackageTypeWizardPage.css';

export type { HostingPackageTypeWizardPageProps };

const CGI_URL = '/manage/manage-package-types.cgi';
const ADD_HOSTING_URL = '/manage/manage-reseller-add.cgi';

const STEPS = [
  { id: 'features', label: 'Package Features' },
  { id: 'web-tools', label: 'Web Tools' },
  { id: 'web-apps', label: 'Web Apps' },
] as const;

type StepId = (typeof STEPS)[number]['id'];
type SubmitAction = 'create-package' | 'create-site';

interface FormState {
  webServerType: WebServerType;
  name: string;
  webspace: number | null;
  bandwidth: number | null;
  databases: number | null;
  subdomains: number | null;
  forwarders: number | null;
  responders: number | null;
  lists: number | null;
  mailboxes: number | null;
  ftpUsers: number | null;
  features: Set<string>;
  installables: Set<string>;
  installAutomatically: string;
  welcomeEmail: number | '';
  extendPasswordResetEmail: number | '';
  extendDomaincpSwitcher: boolean;
}

function firstNumericPreset(options: Array<number | ''>): number | null {
  for (const o of options) {
    if (typeof o === 'number') return o;
  }
  return null;
}

function makeInitialState(props: HostingPackageTypeWizardPageProps): FormState {
  const proto = props.resoldTypeProto;
  return {
    webServerType: 'linux',
    name: props.suggestedName,
    webspace: firstNumericPreset(proto.disc_space),
    bandwidth: firstNumericPreset(proto.bandwidth),
    databases: firstNumericPreset(proto.databasen),
    subdomains: firstNumericPreset(proto.subdomains),
    forwarders: firstNumericPreset(proto.forwarders),
    responders: firstNumericPreset(proto.responders),
    lists: firstNumericPreset(proto.lists),
    mailboxes: firstNumericPreset(proto.mailboxes),
    ftpUsers: firstNumericPreset(proto.ftp_users),
    features: new Set(),
    installables: new Set(),
    installAutomatically: '',
    welcomeEmail: props.emails[0]?.id ?? '',
    extendPasswordResetEmail: props.eprEmails[0]?.id ?? '',
    extendDomaincpSwitcher: false,
  };
}

export const HostingPackageTypeWizardPage: React.FC<HostingPackageTypeWizardPageProps> = (
  props
) => {
  const {
    resoldTypeProto,
    emails,
    eprEmails,
    usedPackageNames,
    hasSnapshots,
    snapshotsEnabled,
    featureCategoriesByType,
    featureToName,
    cgiOrderByType,
    cgiToName,
  } = props;

  const [activeStep, setActiveStep] = useState<StepId>('features');
  const [form, setForm] = useState<FormState>(() => makeInitialState(props));
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const usedNamesLower = useMemo(
    () => new Set(usedPackageNames.map((n) => n.trim().toLowerCase())),
    [usedPackageNames]
  );

  const validateName = useCallback(
    (name: string): string | null => {
      const trimmed = name.trim();
      if (!trimmed) return 'Please enter a package name.';
      if (usedNamesLower.has(trimmed.toLowerCase())) {
        return 'A package with that name already exists.';
      }
      return null;
    },
    [usedNamesLower]
  );

  const validateStepOne = useCallback((): boolean => {
    const err = validateName(form.name);
    setNameError(err);
    return err === null;
  }, [form.name, validateName]);

  const goToStep = useCallback(
    (target: StepId) => {
      const targetIndex = STEPS.findIndex((s) => s.id === target);
      const currentIndex = STEPS.findIndex((s) => s.id === activeStep);
      if (targetIndex <= currentIndex) {
        setActiveStep(target);
        return;
      }
      if (activeStep === 'features' && !validateStepOne()) return;
      setActiveStep(target);
    },
    [activeStep, validateStepOne]
  );

  const handleNext = () => {
    const currentIndex = STEPS.findIndex((s) => s.id === activeStep);
    const next = STEPS[currentIndex + 1];
    /* v8 ignore start -- Next button hidden on the final step, so `next` is always defined */
    if (next) goToStep(next.id);
    /* v8 ignore stop */
  };

  const handleBack = () => {
    const currentIndex = STEPS.findIndex((s) => s.id === activeStep);
    const prev = STEPS[currentIndex - 1];
    /* v8 ignore start -- Back button hidden on the first step, so `prev` is always defined */
    if (prev) goToStep(prev.id);
    /* v8 ignore stop */
  };

  const handleServerTypeChange = (next: WebServerType) => {
    setForm((s) => ({
      ...s,
      webServerType: next,
      // Categories are per-server-type — discard partial selections so stale
      // codes don't carry over to a different OS.
      features: new Set(),
      installables: new Set(),
      installAutomatically: '',
    }));
  };

  const toggleFeature = (code: string, on: boolean) => {
    setForm((s) => {
      const next = new Set(s.features);
      if (on) next.add(code);
      else next.delete(code);
      return { ...s, features: next };
    });
  };

  const toggleInstallable = (code: string, on: boolean) => {
    setForm((s) => {
      const next = new Set(s.installables);
      if (on) next.add(code);
      else next.delete(code);
      return { ...s, installables: next };
    });
  };

  const buildSubmitBody = (action: SubmitAction): URLSearchParams => {
    const body = new URLSearchParams();
    body.set('action', action);
    body.set('id', '0');
    body.set('name', form.name.trim());
    body.set('web_server_type', form.webServerType);
    body.set('webspace', form.webspace === null ? '' : String(form.webspace));
    body.set('bandwidth', form.bandwidth === null ? '' : String(form.bandwidth));
    body.set('databases', form.databases === null ? '' : String(form.databases));
    body.set('subdomains', form.subdomains === null ? '' : String(form.subdomains));
    body.set('forwarders', form.forwarders === null ? '' : String(form.forwarders));
    body.set('responders', form.responders === null ? '' : String(form.responders));
    body.set('lists', form.lists === null ? '' : String(form.lists));
    body.set('mailboxes', form.mailboxes === null ? '' : String(form.mailboxes));
    body.set('ftp_users', form.ftpUsers === null ? '' : String(form.ftpUsers));
    body.set('welcome_email', String(form.welcomeEmail));
    body.set('extend_password_reset_email', String(form.extendPasswordResetEmail));
    if (form.extendDomaincpSwitcher) body.set('extend_domaincp_switcher', 'enabled');
    for (const code of form.features) body.append('features0', code);
    for (const code of form.installables) body.append('installables0', code);
    if (form.installAutomatically) body.append('installables0', form.installAutomatically);
    return body;
  };

  const submit = async (action: SubmitAction) => {
    if (!validateStepOne()) {
      setActiveStep('features');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = buildSubmitBody(action);
      const res = await fetch(CGI_URL, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (!res.ok && res.status !== 302) {
        throw new Error(`Save failed (HTTP ${res.status}).`);
      }
      if (action === 'create-site') {
        const params = new URLSearchParams({
          web_server_type: form.webServerType,
          package_name: form.name.trim(),
        });
        window.location.assign(`${ADD_HOSTING_URL}?${params}`);
      } else {
        window.location.assign(`${CGI_URL}?done=add`);
      }
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err instanceof Error ? err.message : 'Unexpected error while saving.');
    }
  };

  const handleSubmitCreatePackage = (e: React.FormEvent) => {
    e.preventDefault();
    submit('create-package');
  };

  const handleSubmitCreateSite = () => {
    submit('create-site');
  };

  const stepperSteps = STEPS.map((s) => ({
    id: s.id,
    label: s.label,
  }));

  return (
    <div className="hi-ui-hosting-package-type-wizard-page">
      <Text variant="heading-1">Create a Hosting Package</Text>
      <Text variant="body">
        Set up a new web hosting package that you can assign to customers. Once created, you can
        edit it from the list view or assign it to a new hosting account.
      </Text>

      <div className="hi-ui-hosting-package-type-form-step1">
        <FormField label="Web server type" labelPosition="left">
          <Select
            name="web_server_type_selector"
            value={form.webServerType}
            onChange={(e) => handleServerTypeChange(e.target.value as WebServerType)}
            fullWidth
          >
            <option value="linux">Linux</option>
            <option value="windows">Windows</option>
          </Select>
        </FormField>
      </div>

      <form
        onSubmit={handleSubmitCreatePackage}
        className="hi-ui-hosting-package-type-wizard-page__form"
      >
        <input type="hidden" name="id" value="0" />
        <input type="hidden" name="action" value="create-package" />

        <div className="hi-ui-hosting-package-type-form__stepper">
          <Stepper
            steps={stepperSteps}
            currentStep={activeStep}
            onStepChange={(id) => goToStep(id as StepId)}
            completedClickable
          />
        </div>

        {activeStep === 'features' && (
          <div className="hi-ui-hosting-package-type-wizard-page__step">
            <Step1Fields
              serverType={form.webServerType}
              resoldTypeProto={resoldTypeProto}
              emails={emails}
              eprEmails={eprEmails}
              hasSnapshots={hasSnapshots}
              snapshotsEnabled={snapshotsEnabled}
              featuresFieldName="features0"
              values={form}
              nameError={nameError}
              onChange={(patch) => setForm((s) => ({ ...s, ...patch }))}
              onToggleFeature={toggleFeature}
              onClearNameError={() => setNameError(null)}
            />
          </div>
        )}

        {activeStep === 'web-tools' && (
          <div className="hi-ui-hosting-package-type-wizard-page__step">
            <Step2WebTools
              serverType={form.webServerType}
              featureCategoriesByType={featureCategoriesByType}
              featureToName={featureToName}
              featuresFieldName="features0"
              features={form.features}
              onToggleFeature={toggleFeature}
            />
          </div>
        )}

        {activeStep === 'web-apps' && (
          <div className="hi-ui-hosting-package-type-wizard-page__step">
            <Step3WebApps
              serverType={form.webServerType}
              cgiOrderByType={cgiOrderByType}
              cgiToName={cgiToName}
              installablesFieldName="installables0"
              installables={form.installables}
              installAutomatically={form.installAutomatically}
              onToggleInstallable={toggleInstallable}
              onInstallAutomaticallyChange={(value) =>
                setForm((s) => ({ ...s, installAutomatically: value }))
              }
            />
          </div>
        )}

        {submitError && (
          <Notice variant="error" className="hi-ui-hosting-package-type-wizard-page__submit-error">
            {submitError}
          </Notice>
        )}

        <div className="hi-ui-hosting-package-type-wizard-page__footer">
          {activeStep !== 'features' && (
            <Button variant="tertiary" onClick={handleBack} disabled={submitting}>
              Back
            </Button>
          )}
          {activeStep !== 'web-apps' && (
            <Button variant="primary" onClick={handleNext} disabled={submitting}>
              Next
            </Button>
          )}
          {activeStep === 'web-apps' && (
            <>
              <Button
                variant="secondary"
                onClick={handleSubmitCreateSite}
                disabled={submitting}
                loading={submitting}
              >
                Create package and add hosting account
              </Button>
              <Button type="submit" variant="primary" loading={submitting}>
                Create package
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

// `ResoldTypeProto` is imported above for typing; the explicit re-import keeps it
// in the bundle's type surface alongside the page export.
export type { ResoldTypeProto };
